import { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { API_URL } from "../lib/utils";

// Map imports
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default Leaflet marker icons in React/Vite/CRA builds
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const readApiError = async (response) => {
  try {
    const data = await response.json();
    return data?.detail || data?.message || "Request failed.";
  } catch {
    return "Request failed.";
  }
};

export const Contact = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const whatsappNumber = "2349050399235";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
    "Hello GLGIF, I would like to make an enquiry."
  )}`;

  // Uyo approximate coordinates
  const uyoPosition = [5.0377, 7.9128];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!response.ok) {
        const msg = await readApiError(response);
        toast.error(msg);
        return;
      }

      toast.success("Message sent successfully! We will get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Our Location",
      details: ["Uyo, Akwa Ibom State", "Nigeria"],
      color: "bg-red-50 text-red-600",
    },
    {
      icon: Phone,
      title: "Phone / WhatsApp",
      details: ["+2349050399235", "+2347066155981"],
      color: "bg-green-50 text-green-600",
    },
    {
      icon: Mail,
      title: "Email",
      details: ["info@glgif.org"],
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: Clock,
      title: "Office Hours",
      details: ["Mon - Fri: 9:00 AM - 5:00 PM", "Sat: 10:00 AM - 2:00 PM"],
      color: "bg-amber-50 text-amber-600",
    },
  ];

  const socialLinks = [
    { icon: Facebook, href: "https://www.facebook.com/glgifofficial", label: "Facebook" },
    { icon: Instagram, href: "https://www.instagram.com/glgifofficial", label: "Instagram" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/glgifofficial", label: "LinkedIn" },
    { icon: Youtube, href: "https://www.youtube.com/@glgifofficial", label: "YouTube" },
  ];

  return (
    <div data-testid="contact-page" className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-32 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/hero-partner.png')" }}
        />
        <div className="absolute inset-0 bg-stone-900/70" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 z-10">
          <div className="max-w-3xl">
            <p className="font-accent text-xl text-amber-400 mb-4">Get In Touch</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-lg text-stone-200">
              Have questions about our work? Want to partner or volunteer? We'd love to hear from
              you. Reach out and let's create impact together.
            </p>
          </div>
        </div>
      </section>

      {/* Contact cards */}
      <section className="py-12 -mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={info.title} className="card-hover" data-testid={`contact-info-${index}`}>
                <CardContent className="p-6">
                  <div
                    className={`w-12 h-12 ${info.color} rounded-xl flex items-center justify-center mb-4`}
                  >
                    <info.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-2">{info.title}</h3>
                  {info.details.map((detail, i) => (
                    <p key={i} className="text-stone-600 text-sm">
                      {detail}
                    </p>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Form + right panel */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-stone-900">Send Us a Message</h2>
                  <p className="text-stone-500 text-sm">We'll respond within 24-48 hours</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Full Name *</Label>
                    <Input
                      id="contact-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email Address *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-subject">Subject *</Label>
                  <Input
                    id="contact-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    required
                    data-testid="contact-subject"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-message">Message *</Label>
                  <Textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={6}
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-700 hover:bg-amber-800 text-white rounded-full py-6"
                  data-testid="contact-submit"
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>

            <div>
              {/* REAL MAP (no API key needed) */}
              <div className="rounded-2xl overflow-hidden h-80 mb-8 border border-stone-200">
                <MapContainer
                  center={uyoPosition}
                  zoom={13}
                  scrollWheelZoom={false}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={uyoPosition}>
                    <Popup>GLGIF — Uyo, Akwa Ibom State, Nigeria</Popup>
                  </Marker>
                </MapContainer>
              </div>

              <Card>
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-4">Connect With Us</h3>
                  <p className="text-stone-600 mb-6">
                    Follow us on social media for updates, stories, and opportunities to get involved.
                  </p>
                  <div className="flex gap-4">
                    {socialLinks.map((social) => (
                      <a
                        key={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-stone-100 hover:bg-amber-100 rounded-full flex items-center justify-center transition-colors group"
                        aria-label={social.label}
                        data-testid={`social-${social.label.toLowerCase()}`}
                      >
                        <social.icon className="w-5 h-5 text-stone-600 group-hover:text-amber-700" />
                      </a>
                    ))}
                  </div>

                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 bg-stone-900 text-white hover:bg-stone-800 transition-colors"
                    aria-label="Chat with us on WhatsApp"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Chat with us
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Floating chat button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Floating chat button"
        className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 text-white px-5 py-3 shadow-xl transition-all"
      >
        <MessageSquare className="w-5 h-5" />
        <span className="hidden sm:inline font-medium">Chat</span>
      </a>
    </div>
  );
};

export default Contact;
